import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, forkJoin, of, takeUntil } from 'rxjs';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { ToastType } from '../../../../shared/interfaces/common';
import {
  ActiveSubscriptionsForPlan,
  CustomSubConnection,
  CustomSubscriptionPlan,
  SubscriptionConnectionListItem,
} from '../../models/subscription';
import { CommonService } from '../../../../shared/services/common/common.service';
import { SubscriptionService } from '../../services/subscription.service';

type PlanConnectionFilter = 'all' | 'subscribed' | 'trial' | 'not_subscribed';

type PlanConnectionStatusLabel =
  | 'active'
  | 'trialing'
  | 'not_subscribed'
  | 'other_plan';

export interface DisplayAdminUser {
  id?: number;
  name: string;
  email?: string;
}

interface PlanConnectionRow {
  connection: CustomSubConnection;
  subscriptionOnPlan?: ActiveSubscriptionsForPlan;
  canStartTrial: boolean;
  statusLabel: PlanConnectionStatusLabel;
}

@Component({
  selector: 'app-view-subscription-plan',
  standalone: true,
  imports: [CommonModule, DatePipe, PopupComponent, RouterLink],
  templateUrl: './view-subscription-plan.component.html',
  styleUrls: ['./view-subscription-plan.component.scss'],
})
export class ViewSubscriptionPlanComponent
  extends MenuComponentBase
  implements OnInit
{
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private subscriptionService = inject(SubscriptionService);
  private commonService = inject(CommonService);

  planName = '';
  selectedPlan: CustomSubscriptionPlan | null = null;
  connectionsList: SubscriptionConnectionListItem[] = [];
  activeSubscriptionsForPlan: ActiveSubscriptionsForPlan[] = [];
  planConnectionRows: PlanConnectionRow[] = [];
  planConnectionFilter: PlanConnectionFilter = 'all';

  isLoading = true;
  isArchiving = false;
  showConfirmPopup = false;
  startingTrialConnectionName: string | null = null;
  showTrialConfirmPopup = false;
  trialConfirmConnection: CustomSubConnection | null = null;
  viewAdminsRow: PlanConnectionRow | null = null;

  get hasActiveSubscriptions(): boolean {
    if (!this.selectedPlan) {
      return false;
    }

    const currentPlanName = this.selectedPlan.plan_name?.trim().toLowerCase();

    const hasInActiveSubs = this.activeSubscriptionsForPlan.some(
      (subscription) =>
        ['active', 'trialing'].includes(
          subscription.status?.toLowerCase() ?? '',
        ),
    );

    const hasInConnections = (this.selectedPlan.connections ?? []).some(
      (connection) => {
        const isStatusActiveOrTrialing = ['active', 'trialing'].includes(
          connection.subscription_status?.toLowerCase() ?? '',
        );
        const isThisPlan =
          !!connection.active_plan_name &&
          connection.active_plan_name.trim().toLowerCase() === currentPlanName;
        return isStatusActiveOrTrialing && isThisPlan;
      },
    );

    return hasInActiveSubs || hasInConnections;
  }

  get isArchiveDisabled(): boolean {
    if (this.isArchiving) {
      return true;
    }
    if (!this.selectedPlan) {
      return true;
    }
    return !!this.selectedPlan.is_active && this.hasActiveSubscriptions;
  }

  get archiveButtonTitle(): string {
    if (!this.selectedPlan) {
      return '';
    }
    if (this.isArchiving) {
      return this.selectedPlan.is_active
        ? 'Archiving plan...'
        : 'Unarchiving plan...';
    }
    if (this.selectedPlan.is_active) {
      if (this.hasActiveSubscriptions) {
        return 'Cannot archive plan with active or trialing subscriptions';
      }
      return 'Archive Plan';
    }
    return 'Unarchive Plan';
  }

  private readonly HIDDEN_PERMISSION_CODENAMES: readonly string[] = [
    PermissionCode.MANAGE_SUBSCRIPTION,
    PermissionCode.ADMIN_PRIVILEGES,
    PermissionCode.ACCESS_DNB,
    PermissionCode.SEARCH_BY_SOURCE,
    PermissionCode.DEVELOPER_PRIVILEGES,
    PermissionCode.ACCESS_VLF,
    'manage_subscription',
    'admin_privileges',
    'access_dnb',
    'search_by_source',
    'developer_privileges',
    'access_vlf',
  ];

  get planFeatures(): string[] {
    if (!this.selectedPlan) {
      return [];
    }

    const featureNames = new Set<string>();

    if (Array.isArray(this.selectedPlan.marketing_features)) {
      for (const item of this.selectedPlan.marketing_features) {
        const name = typeof item === 'string' ? item : item?.name;
        if (name && typeof name === 'string' && name.trim()) {
          featureNames.add(name.trim());
        }
      }
    }

    if (Array.isArray(this.selectedPlan.permissions)) {
      for (const perm of this.selectedPlan.permissions) {
        const codename = perm?.codename || '';
        if (this.HIDDEN_PERMISSION_CODENAMES.includes(codename)) {
          continue;
        }
        if (perm?.name && typeof perm.name === 'string' && perm.name.trim()) {
          featureNames.add(perm.name.trim());
        }
      }
    }

    return Array.from(featureNames);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Ensure menu highlights Subscription Plans
    const menuItem = this.menuService.getMenuItem(
      'configs/manage-subscription-plans',
    );
    if (menuItem) {
      this.menuService.menuOnChange(menuItem);
    }

    this.planName =
      this.activatedRoute.snapshot.paramMap.get('plan_name') || '';
    if (!this.planName) {
      this.commonService.triggerToast({
        type: ToastType.Error,
        message: 'No subscription plan specified.',
      });
      this.goBack();
      return;
    }

    this.loadPlanDetails(this.planName);

    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const nextPlan = params.get('plan_name') || '';
        if (nextPlan && nextPlan !== this.planName) {
          this.planName = nextPlan;
          this.loadPlanDetails(nextPlan);
        }
      });
  }

  loadPlanDetails(planName: string): void {
    this.isLoading = true;
    forkJoin({
      planDetail: this.subscriptionService.getSubscriptionPlanDetail(planName),
      connections: this.subscriptionService
        .getSubscriptionConnections(1, 100)
        .pipe(catchError(() => of({ results: [] }))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.selectedPlan = results.planDetail;
          this.connectionsList = results.connections?.results || [];
          this.activeSubscriptionsForPlan =
            results.planDetail.active_subscriptions ?? [];
          this.planConnectionRows = this.buildPlanConnectionRows();
          this.planConnectionFilter = 'all';

          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              error?.detail || error?.[0] || 'Failed to load plan details.',
          });
        },
      });
  }

  goBack(): void {
    const returnUrl = (window.history.state as { returnUrl?: string })
      ?.returnUrl;
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
      return;
    }

    this.router.navigate(['/configs/manage-subscription-plans']);
  }

  onEditPlan(): void {
    if (!this.selectedPlan || !this.selectedPlan.is_active) {
      return;
    }
    const returnUrl = (window.history.state as { returnUrl?: string })
      ?.returnUrl;
    this.router.navigate(
      ['/configs/manage-subscription-plans/edit', this.selectedPlan.plan_name],
      {
        state: {
          fromView: true,
          returnUrl,
        },
      },
    );
  }

  openArchiveConfirm(): void {
    if (this.isArchiveDisabled) {
      return;
    }
    this.showConfirmPopup = true;
  }

  closeArchiveConfirm(): void {
    if (this.isArchiving) {
      return;
    }
    this.showConfirmPopup = false;
  }

  onConfirmArchive(): void {
    if (
      !this.selectedPlan ||
      (this.selectedPlan.is_active && this.hasActiveSubscriptions) ||
      this.isArchiving
    ) {
      return;
    }

    this.isArchiving = true;
    const nextStatus = !this.selectedPlan.is_active;

    this.subscriptionService
      .updateCustomSubscriptionPlan(
        { is_active: nextStatus },
        this.selectedPlan.plan_name,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isArchiving = false;
          this.showConfirmPopup = false;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: nextStatus
              ? 'Successfully unarchived subscription plan.'
              : 'Successfully archived subscription plan.',
          });
          this.loadPlanDetails(this.selectedPlan!.plan_name);
        },
        error: (error) => {
          this.isArchiving = false;
          this.showConfirmPopup = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              error?.detail ||
              error?.[0] ||
              'Failed to update subscription plan status.',
          });
        },
      });
  }

  setPlanConnectionFilter(filter: PlanConnectionFilter): void {
    this.planConnectionFilter = filter;
  }

  getFilteredPlanConnectionRows(): PlanConnectionRow[] {
    return this.planConnectionRows.filter((row) =>
      this.rowMatchesFilter(row, this.planConnectionFilter),
    );
  }

  getPlanConnectionTabCounts(): Record<PlanConnectionFilter, number> {
    const filters: PlanConnectionFilter[] = [
      'all',
      'subscribed',
      'trial',
      'not_subscribed',
    ];
    return filters.reduce(
      (counts, filter) => {
        counts[filter] = this.planConnectionRows.filter((row) =>
          this.rowMatchesFilter(row, filter),
        ).length;
        return counts;
      },
      {} as Record<PlanConnectionFilter, number>,
    );
  }

  canShowTrialActions(): boolean {
    return (
      !!this.selectedPlan?.is_active &&
      (this.selectedPlan?.trial_period_days ?? 0) > 0
    );
  }

  getPlanConnectionEmptyMessage(): string {
    switch (this.planConnectionFilter) {
      case 'subscribed':
        return 'No subscribed connections on this plan.';
      case 'trial':
        return 'No connections can start a trial on this plan.';
      case 'not_subscribed':
        return 'No eligible connections are waiting to subscribe.';
      default:
        return 'No connections eligible for this plan.';
    }
  }

  getConnectionStatusText(row: PlanConnectionRow): string {
    if (row.statusLabel === 'other_plan') {
      return row.connection.active_plan_name
        ? `On ${row.connection.active_plan_name}`
        : 'On other plan';
    }
    if (row.statusLabel === 'not_subscribed') {
      return 'Not subscribed';
    }
    return row.statusLabel === 'trialing' ? 'Trialing' : 'Active';
  }

  getTrialActionTitle(row: PlanConnectionRow): string {
    if (row.canStartTrial) {
      return 'Start a trial for this connection';
    }
    if (row.statusLabel === 'other_plan') {
      return 'Already subscribed on another plan';
    }
    return 'Already subscribed on this plan';
  }

  openTrialConfirm(connection: CustomSubConnection): void {
    this.trialConfirmConnection = connection;
    this.showTrialConfirmPopup = true;
  }

  closeTrialConfirm(): void {
    if (this.startingTrialConnectionName) {
      return;
    }
    this.showTrialConfirmPopup = false;
    this.trialConfirmConnection = null;
  }

  onConfirmStartTrial(): void {
    if (
      !this.trialConfirmConnection ||
      !this.selectedPlan ||
      this.startingTrialConnectionName
    ) {
      return;
    }

    const connectionName = this.trialConfirmConnection.connection_name;
    this.startingTrialConnectionName = connectionName;
    this.subscriptionService
      .startTrial({
        plan_name: this.selectedPlan.plan_name,
        connection_name: connectionName,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.startingTrialConnectionName = null;
          this.showTrialConfirmPopup = false;
          this.trialConfirmConnection = null;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Trial started successfully.',
          });
          this.loadPlanDetails(this.selectedPlan!.plan_name);
        },
        error: (error) => {
          this.startingTrialConnectionName = null;
          this.showTrialConfirmPopup = false;
          this.trialConfirmConnection = null;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: error?.detail || error?.[0] || 'Failed to start trial.',
          });
        },
      });
  }

  navigateToPlan(targetPlanName: string, event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    if (!targetPlanName) {
      return;
    }
    if (targetPlanName === this.planName) {
      return;
    }
    this.router.navigate([
      '/configs/manage-subscription-plans/view',
      targetPlanName,
    ]);
    this.planName = targetPlanName;
    this.loadPlanDetails(targetPlanName);
  }

  getSubscriptionPeriodEnd(row: PlanConnectionRow): Date | string | null {
    const sub = row.subscriptionOnPlan;
    const val = sub?.current_period_end || sub?.trial_end;
    if (!val) {
      return null;
    }
    if (typeof val === 'number') {
      return val < 10000000000 ? new Date(val * 1000) : new Date(val);
    }
    if (typeof val === 'string' && /^\d+$/.test(val.trim())) {
      const num = Number(val.trim());
      return num < 10000000000 ? new Date(num * 1000) : new Date(num);
    }
    return val;
  }

  private buildPlanConnectionRows(): PlanConnectionRow[] {
    const subscriptionsByConnection = new Map<
      string,
      ActiveSubscriptionsForPlan
    >();

    for (const subscription of this.activeSubscriptionsForPlan) {
      if (!subscription?.connection_name) {
        continue;
      }
      const key = subscription.connection_name.trim().toLowerCase();
      const existing = subscriptionsByConnection.get(key);
      const isCurrentActive = ['active', 'trialing'].includes(
        subscription.status?.toLowerCase() ?? '',
      );
      if (!existing || isCurrentActive) {
        subscriptionsByConnection.set(key, subscription);
      }
    }

    const connectionRows: PlanConnectionRow[] = (
      this.selectedPlan?.connections ?? []
    ).map((connection) => {
      const connKey = connection.connection_name?.trim().toLowerCase();
      const subscriptionOnPlan = connKey
        ? subscriptionsByConnection.get(connKey)
        : undefined;

      const subStatusOnPlan = subscriptionOnPlan?.status?.toLowerCase();
      const connGloballySubscribed = ['active', 'trialing'].includes(
        connection.subscription_status?.toLowerCase() ?? '',
      );
      const isActivePlanThisPlan =
        !!connection.active_plan_name &&
        connection.active_plan_name.trim().toLowerCase() ===
          this.selectedPlan?.plan_name?.trim().toLowerCase();

      let statusLabel: PlanConnectionStatusLabel = 'not_subscribed';
      if (
        subStatusOnPlan === 'trialing' ||
        (isActivePlanThisPlan &&
          connection.subscription_status?.toLowerCase() === 'trialing')
      ) {
        statusLabel = 'trialing';
      } else if (
        subStatusOnPlan === 'active' ||
        (isActivePlanThisPlan &&
          connection.subscription_status?.toLowerCase() === 'active')
      ) {
        statusLabel = 'active';
      } else if (connGloballySubscribed) {
        statusLabel = 'other_plan';
      }

      const isSubscribedOnThisPlan =
        statusLabel === 'active' || statusLabel === 'trialing';

      const isGloballySubscribed =
        isSubscribedOnThisPlan || connGloballySubscribed;

      return {
        connection: {
          ...connection,
          display_name:
            connection.display_name ||
            subscriptionOnPlan?.connection_display_name ||
            connection.connection_name,
          subscription_admin_name:
            connection.subscription_admin_name ||
            subscriptionOnPlan?.subscription_admin_name ||
            null,
          subscription_admin_email:
            connection.subscription_admin_email ||
            subscriptionOnPlan?.subscription_admin_email ||
            null,
          subscription_admins:
            connection.subscription_admins ||
            subscriptionOnPlan?.subscription_admins ||
            [],
        },
        subscriptionOnPlan,
        canStartTrial: this.canShowTrialActions() && !isGloballySubscribed,
        statusLabel,
      };
    });

    const existingConnectionNames = new Set(
      (this.selectedPlan?.connections ?? [])
        .map((c) => c.connection_name?.trim().toLowerCase())
        .filter(Boolean),
    );

    const activeSubOnlyRows: PlanConnectionRow[] = [];
    const seenActiveSubConn = new Set<string>();

    for (const subscription of this.activeSubscriptionsForPlan) {
      if (!subscription?.connection_name) {
        continue;
      }
      const key = subscription.connection_name.trim().toLowerCase();
      if (existingConnectionNames.has(key) || seenActiveSubConn.has(key)) {
        continue;
      }
      seenActiveSubConn.add(key);

      const normStatus = subscription.status?.toLowerCase() ?? '';
      const statusLabel: PlanConnectionStatusLabel =
        normStatus === 'trialing' ? 'trialing' : 'active';

      const connection: CustomSubConnection = {
        connection_id: subscription.connection_id ?? subscription.id ?? 0,
        connection_name: subscription.connection_name,
        display_name:
          subscription.connection_display_name || subscription.connection_name,
        subscription_admin_user_id: null,
        subscription_admin_name: subscription.subscription_admin_name ?? null,
        subscription_admin_email: subscription.subscription_admin_email ?? null,
        subscription_status: subscription.status,
        active_plan_name:
          subscription.plan_name || this.selectedPlan?.plan_name || null,
      };

      activeSubOnlyRows.push({
        connection,
        subscriptionOnPlan: subscription,
        canStartTrial: false,
        statusLabel,
      });
    }

    const allRows = [...activeSubOnlyRows, ...connectionRows];

    allRows.sort((a, b) => {
      const aSubscribed =
        a.statusLabel === 'active' || a.statusLabel === 'trialing';
      const bSubscribed =
        b.statusLabel === 'active' || b.statusLabel === 'trialing';
      if (aSubscribed && !bSubscribed) {
        return -1;
      }
      if (!aSubscribed && bSubscribed) {
        return 1;
      }
      return 0;
    });

    return allRows;
  }

  private rowMatchesFilter(
    row: PlanConnectionRow,
    filter: PlanConnectionFilter,
  ): boolean {
    if (filter === 'subscribed') {
      return row.statusLabel === 'active' || row.statusLabel === 'trialing';
    }
    if (filter === 'trial') {
      return row.canStartTrial;
    }
    if (filter === 'not_subscribed') {
      return (
        row.statusLabel === 'not_subscribed' || row.statusLabel === 'other_plan'
      );
    }
    return true;
  }

  getAdminsForRow(row: PlanConnectionRow): DisplayAdminUser[] {
    const conn = row.connection;
    const connName = conn.connection_name;
    const subOnPlan = row.subscriptionOnPlan;

    const adminsMap = new Map<string, DisplayAdminUser>();

    // 1. From connectionsList (fetched from getSubscriptionConnections API)
    const fromList = this.connectionsList.find(
      (c) => c.connection_name?.toLowerCase() === connName?.toLowerCase(),
    );
    if (
      fromList?.subscription_admins &&
      fromList.subscription_admins.length > 0
    ) {
      for (const u of fromList.subscription_admins) {
        const name =
          `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
          u.email ||
          `Admin #${u.id}`;
        const key = u.id ? String(u.id) : (u.email || name).toLowerCase();
        adminsMap.set(key, { id: u.id, name, email: u.email });
      }
    }

    // 2. From plan connection subscription_admins
    if (conn.subscription_admins && conn.subscription_admins.length > 0) {
      for (const u of conn.subscription_admins) {
        const name =
          `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
          u.email ||
          `Admin #${u.id}`;
        const key = u.id ? String(u.id) : (u.email || name).toLowerCase();
        if (!adminsMap.has(key)) {
          adminsMap.set(key, { id: u.id, name, email: u.email });
        }
      }
    }

    // 3. From subscription on plan subscription_admins
    if (
      subOnPlan?.subscription_admins &&
      subOnPlan.subscription_admins.length > 0
    ) {
      for (const u of subOnPlan.subscription_admins) {
        const name =
          `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
          u.email ||
          `Admin #${u.id}`;
        const key = u.id ? String(u.id) : (u.email || name).toLowerCase();
        if (!adminsMap.has(key)) {
          adminsMap.set(key, { id: u.id, name, email: u.email });
        }
      }
    }

    // 4. Fallback to single subscription_admin_name / email if map is empty
    if (adminsMap.size === 0) {
      const singleName =
        conn.subscription_admin_name || subOnPlan?.subscription_admin_name;
      const singleEmail =
        conn.subscription_admin_email || subOnPlan?.subscription_admin_email;

      if (singleName || singleEmail) {
        adminsMap.set('single', {
          name: singleName || singleEmail || 'Admin',
          email: singleEmail || undefined,
        });
      }
    }

    return Array.from(adminsMap.values());
  }

  getVisibleAdmins(row: PlanConnectionRow, limit = 2): DisplayAdminUser[] {
    return this.getAdminsForRow(row).slice(0, limit);
  }

  getHiddenAdminsCount(row: PlanConnectionRow, limit = 2): number {
    const total = this.getAdminsForRow(row).length;
    return total > limit ? total - limit : 0;
  }

  getAdminsTooltip(row: PlanConnectionRow): string {
    const admins = this.getAdminsForRow(row);
    return admins
      .map((a) => (a.email ? `${a.name} (${a.email})` : a.name))
      .join(', ');
  }

  openViewAdmins(row: PlanConnectionRow): void {
    this.viewAdminsRow = row;
  }

  closeViewAdmins(): void {
    this.viewAdminsRow = null;
  }
}
